// UI-only state for the requests feature. Reads come from the wire via
// TanStack Query (`useRequest`/`useRequestsList`), mutations route through
// the per-id mutation hooks in `queries.ts`. This store remembers the
// drawer-open state (`selectedId`), the active modal (`modal`), and the
// editable draft scratchpad — a client-side draft owned by the "+" flow
// and by the persisted-DRAFT reopen flow. The scratchpad maps to a wire
// row via the aggregate save endpoints (#521): the first save creates a
// persisted DRAFT, subsequent saves update it, and Send transitions it
// to IN_WORK. Discard runs photo cleanup against the keys that never
// reached the saved baseline.
//
// @aiPurpose Holds drawer/modal UI state and the editable-draft
//   scratchpad for the requests feature.
// @aiInvariant `draft.items[*].photos` carries at most
//   `REQUEST_ITEM_PHOTOS_MAX` storage keys per item (#1451), matching
//   `RequestItemSchema.photos.max(REQUEST_ITEM_PHOTOS_MAX)`. SIMPLE rows
//   append/remove via `addDraftItemPhoto`/`removeDraftItemPhoto`; the
//   single-photo `setDraftItemPhoto` stays for non-SIMPLE rows.
// @aiInvariant `baselineDraft` mirrors the server-saved state so dirty
//   detection is exact: a header/item/photo edit that brings the
//   scratchpad back to the baseline value collapses dirty back to
//   false. After a successful save, `setBaselineFromSave` rewrites the
//   scratchpad's `clientItemId → it-NNNN` mapping AND snapshots the
//   post-save state as the new baseline so the next dirty check
//   compares against the row that actually exists on the server.

import { create } from 'zustand';
import {
  nextClientBoxId,
  nextClientBoxItemId,
  nextClientPackageId,
  nextDraftItemId,
} from './draft-ids.js';
import { cloneDraft, computeApplySaveResult } from './draft-helpers.js';
import { buildFreshDraftScratchpad } from './draft-seed.js';
import { buildPersistedDraftScratchpad } from './draft-from-persisted-row.js';
import {
  addBox as boxAdd,
  patchBox as boxPatch,
  removeBox as boxRemove,
  addBoxItem as boxItemAdd,
  patchBoxItem as boxItemPatch,
  removeBoxItem as boxItemRemove,
  setBoxItemPhoto as boxItemSetPhoto,
} from './draft-box-actions.js';
import {
  addItemPhoto as itemPhotoAdd,
  removeItemPhoto as itemPhotoRemove,
} from './draft-item-photo-actions.js';
import { addItem as itemAdd, duplicateItem as itemDuplicate } from './draft-item-actions.js';
import { beginActivity, settleActivity, uploadGuardFor } from './draft-activity.js';
import type { RequestsUiState } from './store-types.js';

export { isDraftDirty } from './draft-helpers.js';
export { nextClientBoxId, nextClientBoxItemId, nextClientPackageId } from './draft-ids.js';

export const useRequestsStore = create<RequestsUiState>((set) => ({
  selectedId: null,
  modal: null,
  draft: null,
  draftPersistedId: null,
  baselineDraft: null,
  unsavedPhotoKeys: [],
  draftActivity: 'idle',
  pendingPhotoUploads: 0,
  pendingDraftCloseFollowUp: null,

  select: (id) => set({ selectedId: id }),
  setModal: (m) => set({ modal: m }),

  // WHY: the cutover product spec (#317 layer 8b-3) hard-codes the
  // China→Russia corridor as the default route on a new draft so the
  // logist does not retype it on the common case. The values are
  // server-bound payload, not display strings — the i18n parity check
  // in `apps/web/src/i18n/locales/**` only governs UI copy, not these.
  //
  // WHY clientToken minting here: #415 slice 2's create-idempotency
  // contract requires the SAME token on every retry of a given Send
  // attempt. Minting in `openDraft` (when the user clicks "+ Новая
  // заявка") gives the token the same lifetime as the scratchpad —
  // every subsequent Send/retry within this scratchpad sends the
  // same value, the server's partial UNIQUE INDEX catches the
  // second-and-later attempts, and the resolve branch returns the
  // already-committed row instead of creating duplicates. Reset
  // happens only in `closeDraft`, so even the freeze-on-success
  // path (PR #423) keeps the token until the user explicitly
  // dismisses the scratchpad.
  openDraft: (manager, defaults) =>
    set({
      // WHY (#1393): the seed object was extracted to `draft-seed.ts` to
      //   keep this store under the hand-written file-size limit. The
      //   `clientToken` is still minted here so its lifetime matches the
      //   scratchpad (see the idempotency note above).
      draft: buildFreshDraftScratchpad({
        manager,
        clientToken: crypto.randomUUID(),
        destinations: defaults?.destinations ?? [],
        kind: defaults?.kind ?? 'STANDARD',
        // WHY (#1599): forward the pre-bound funnel link only when the
        //   caller supplied it so the "+" path keeps the keys absent.
        ...(defaults?.leadId !== undefined ? { leadId: defaults.leadId } : {}),
        ...(defaults?.dealId !== undefined ? { dealId: defaults.dealId } : {}),
      }),
      draftPersistedId: null,
      baselineDraft: null,
      unsavedPhotoKeys: [],
      draftActivity: 'idle',
      pendingPhotoUploads: 0,
    }),

  // WHY (#521 reopen): persisted DRAFT row → editor. The row payload
  // already carries server `it-NNNN` ids on every item, so the local
  // scratchpad reuses them directly without going through
  // nextDraftItemId(); subsequent edits keep the id stable so the
  // next save's diff lands UPDATEs (not CREATEs). Baseline is set to
  // a clone of the same scratchpad so dirty starts false.
  openPersistedDraft: ({ row, manager }) => {
    const seeded = buildPersistedDraftScratchpad(row, manager);
    set({
      draft: seeded,
      draftPersistedId: row.id,
      baselineDraft: cloneDraft(seeded),
      unsavedPhotoKeys: [],
      draftActivity: 'idle',
      pendingPhotoUploads: 0,
    });
  },

  patchDraftHeader: (p) => set((s) => (s.draft ? { draft: { ...s.draft, ...p } } : s)),

  setDraftDestinations: (destinations) =>
    set((s) => (s.draft ? { draft: { ...s.draft, destinations } } : s)),

  setDraftRoadOptionIds: (ids) =>
    set((s) => (s.draft ? { draft: { ...s.draft, roadOptionIds: ids } } : s)),

  setDraftPackagingOptionIds: (ids) =>
    set((s) => (s.draft ? { draft: { ...s.draft, packagingOptionIds: ids } } : s)),

  addDraftItem: () => {
    const id = nextDraftItemId();
    set((s) => {
      const next = itemAdd(s.draft, id);
      return next ? { draft: next } : s;
    });
    return id;
  },

  duplicateDraftItem: (itemId) =>
    set((s) => {
      const next = itemDuplicate(s.draft, itemId, nextDraftItemId(), nextClientPackageId);
      return next ? { draft: next } : s;
    }),

  patchDraftItem: (itemId, p) =>
    set((s) =>
      s.draft
        ? {
            draft: {
              ...s.draft,
              items: s.draft.items.map((it) => (it.id === itemId ? { ...it, ...p } : it)),
            },
          }
        : s,
    ),

  removeDraftItem: (itemId) =>
    set((s) =>
      s.draft
        ? { draft: { ...s.draft, items: s.draft.items.filter((it) => it.id !== itemId) } }
        : s,
    ),

  // WHY (#599): explicit ownership flip for the boxes editor section.
  //   The save adapter consults `boxesSectionOpen` (not `boxes.length`)
  //   to decide whether to echo `boxes` on the wire. Calling this is
  //   idempotent — re-opens never re-flip the flag.
  openBoxesSection: () =>
    set((s) =>
      s.draft && !s.draft.boxesSectionOpen ? { draft: { ...s.draft, boxesSectionOpen: true } } : s,
    ),

  addDraftBox: () => {
    const clientBoxId = nextClientBoxId();
    set((s) => {
      const next = boxAdd(s.draft, clientBoxId);
      return next ? { draft: next } : s;
    });
    return clientBoxId;
  },

  patchDraftBox: (boxClientId, p) =>
    set((s) => {
      const next = boxPatch(s.draft, boxClientId, p);
      return next ? { draft: next } : s;
    }),

  removeDraftBox: (boxClientId) =>
    set((s) => {
      const next = boxRemove(s.draft, boxClientId);
      return next ? { draft: next } : s;
    }),

  addDraftBoxItem: (boxClientId) => {
    const clientBoxItemId = nextClientBoxItemId();
    set((s) => {
      const next = boxItemAdd(s.draft, boxClientId, clientBoxItemId);
      return next ? { draft: next } : s;
    });
    return clientBoxItemId;
  },

  patchDraftBoxItem: (boxClientId, boxItemClientId, p) =>
    set((s) => {
      const next = boxItemPatch(s.draft, boxClientId, boxItemClientId, p);
      return next ? { draft: next } : s;
    }),

  removeDraftBoxItem: (boxClientId, boxItemClientId) =>
    set((s) => {
      const next = boxItemRemove(s.draft, boxClientId, boxItemClientId);
      return next ? { draft: next } : s;
    }),

  setDraftBoxItemPhoto: (boxClientId, boxItemClientId, key) =>
    set((s) => {
      const next = boxItemSetPhoto(s.draft, boxClientId, boxItemClientId, key);
      return next ? { draft: next } : s;
    }),

  setDraftItemPhoto: (itemId, key) =>
    set((s) =>
      s.draft
        ? {
            draft: {
              ...s.draft,
              items: s.draft.items.map((it) =>
                it.id === itemId ? { ...it, photos: key === null ? [] : [key] } : it,
              ),
            },
          }
        : s,
    ),

  addDraftItemPhoto: (itemId, key) =>
    set((s) => {
      const next = itemPhotoAdd(s.draft, itemId, key);
      return next ? { draft: next } : s;
    }),

  removeDraftItemPhoto: (itemId, key) =>
    set((s) => {
      const next = itemPhotoRemove(s.draft, itemId, key);
      return next ? { draft: next } : s;
    }),

  // INVARIANT: tracker is best-effort dedup (a re-tracked key during a
  // transient retry is harmless). On save-success, baseline-photo
  // intersection narrows the set; on discard, the entire set is
  // shipped to the cleanup endpoints so storage doesn't accumulate
  // orphans. Empty key is a no-op so a caller that hands null/empty
  // strings does not pollute the cleanup batch.
  trackUnsavedPhoto: (key) =>
    set((s) => {
      if (!key) return s;
      if (s.unsavedPhotoKeys.includes(key)) return s;
      return { unsavedPhotoKeys: [...s.unsavedPhotoKeys, key] };
    }),

  beginPhotoUpload: () => set((s) => ({ pendingPhotoUploads: s.pendingPhotoUploads + 1 })),
  endPhotoUpload: () =>
    set((s) => ({ pendingPhotoUploads: Math.max(0, s.pendingPhotoUploads - 1) })),

  setDraftPersistedId: (id) => set({ draftPersistedId: id }),
  // WHY (#1594): guard from draft-activity.ts — close/delete require zero pending uploads.
  beginDraftActivity: (event) => beginActivity(set, event, uploadGuardFor(event)),
  settleDraftActivity: () => settleActivity(set),
  setPendingDraftCloseFollowUp: (action) => set({ pendingDraftCloseFollowUp: action }),
  applySaveResult: (input) =>
    set((s) => {
      if (!s.draft) return s;
      return computeApplySaveResult(s.draft, s.unsavedPhotoKeys, input);
    }),

  closeDraft: () =>
    set({
      draft: null,
      draftPersistedId: null,
      baselineDraft: null,
      unsavedPhotoKeys: [],
      draftActivity: 'idle',
      pendingPhotoUploads: 0,
      pendingDraftCloseFollowUp: null,
    }),
}));
